import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { formatDate, formatMs } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/status-badge';
import type { ConversationStatus, ConversationSummary } from '@/lib/types';

const STATUS_TONE: Record<ConversationStatus, 'green' | 'rose' | 'amber'> = {
  ok: 'green',
  error: 'rose',
  blocked: 'amber',
};

export function Conversations(): JSX.Element {
  const navigate = useNavigate();
  const turns = useQuery({
    queryKey: ['/api/admin/conversations'],
    queryFn: () => api.get<ConversationSummary[]>('/api/admin/conversations?limit=200'),
  });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Conversations</h2>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Időpont</th>
              <th className="px-4 py-2.5">Deployment</th>
              <th className="px-4 py-2.5">Modell</th>
              <th className="px-4 py-2.5" title="NER időtartam">NER</th>
              <th className="px-4 py-2.5" title="OCR időtartam">OCR</th>
              <th className="px-4 py-2.5">Állapot</th>
              <th className="px-4 py-2.5">Kicserélt előnézet</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {turns.isLoading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Betöltés…</td></tr>
            )}
            {turns.isError && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-rose-600">Hiba: {String(turns.error)}</td></tr>
            )}
            {turns.data?.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500">Nincs beszélgetés.</td></tr>
            )}
            {turns.data?.map((t) => (
              <tr
                key={t.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => navigate(`/conversations/${t.id}`)}
              >
                <td className="whitespace-nowrap px-4 py-2.5">{formatDate(t.occurredAt)}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{t.deploymentSlug}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{t.modelName}</td>
                <td className="px-4 py-2.5 tabular-nums">{formatMs(t.nerMs)}</td>
                <td className="px-4 py-2.5 tabular-nums">{formatMs(t.ocrMs)}</td>
                <td className="px-4 py-2.5">
                  <StatusBadge label={t.status} tone={STATUS_TONE[t.status]} />
                </td>
                <td className="max-w-md truncate px-4 py-2.5 text-slate-500">{t.swappedPreview}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
